import * as React from 'react';
import { LoadMoreSentinel } from './LoadMoreSentinel';
import { PaginatedSelectInput } from './PaginatedSelectInput';
import { PaginatedSelectItem } from './PaginatedSelectItem';
import { PaginatedSelectContentVariants } from './paginated-select.variants';
import { usePaginatedSelectContext } from './usePaginatedSelectContext';
import { cn } from '@/shared/lib/cn';

interface PaginatedSelectContentProps {
  children?: React.ReactNode;
  className?: string;
}

function PaginatedSelectContent({ className }: PaginatedSelectContentProps) {
  const ctx = usePaginatedSelectContext();
  const { open, setOpen, items, isLoading, isFetchingMore, hasMore, enableSearch, onLoadMore } =
    ctx as {
      open: boolean;
      setOpen: (open: boolean) => void;
      items: Array<Record<string, unknown>>;
      isLoading: boolean;
      isFetchingMore: boolean;
      hasMore: boolean;
      enableSearch: boolean;
      onLoadMore: () => void;
    };

  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contentRef.current && !contentRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, setOpen]);

  if (!open) return null;

  return (
    <div
      ref={contentRef}
      className={cn(
        PaginatedSelectContentVariants({
          class: className,
        }),
        'max-h-[300px] overflow-hidden flex flex-col',
      )}
    >
      {enableSearch && (
        <div className="p-2 border-b border-outline">
          <PaginatedSelectInput placeholder="Buscar..." />
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-1">
        {isLoading && items.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <svg
              className="h-8 w-8 mb-2 opacity-50"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <p className="text-sm">No se encontraron resultados</p>
          </div>
        ) : (
          // react-doctor-disable-next-line react-doctor/prefer-tag-over-role -- custom paginated listbox; no native <select> equivalent for this infinite-scroll widget
          <div role="listbox" className="space-y-0.5 mb-4">
            {items.map((item, index) => (
              <PaginatedSelectItem key={String(item.id || index)} item={item} />
            ))}
          </div>
        )}

        {isFetchingMore && (
          <div className="sticky bottom-0 bg-select-background border-t border-outline py-4 flex items-center justify-center">
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5 animate-spin text-primary" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <span className="text-sm text-muted-foreground">Cargando más...</span>
            </div>
          </div>
        )}

        {!hasMore && items.length > 0 && (
          <div className="text-center py-2 text-xs text-muted-foreground">
            No hay más resultados
          </div>
        )}

        {hasMore && !isFetchingMore && items.length > 0 && (
          <LoadMoreSentinel onLoadMore={onLoadMore} isFetching={isFetchingMore} hasMore={hasMore} />
        )}
      </div>
    </div>
  );
}

export { PaginatedSelectContent };
